import clsx from 'clsx';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import HoverPrefetchLink from '../hover-prefetch-link';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const sortQueryMap = {
  created_at: 'new',
  position: 'featured',
  best_seller: 'best_sellers',
  most_reviews: 'top_rated',
  low_to_high: 'price_low',
  high_to_low: 'price_high',
};

const ProductSort = props => {
  const { availableSortMethods, sortState, isSearchPage } = props;

  const [open, setOpen] = useState(false);

  const { locale } = useIntl();
  const searchParams = useSearchParams();
  const urlHasSort = searchParams.has('sort_by');
  const pathname = usePathname();
  const excludePaginationPathParts = pathname.split('/').filter((x, i, arr) => {
    return !!x && x !== 'p' && (!/\d+/.test(x) || (/\d+/.test(x) && i > 0 && arr[i - 1] !== 'p'));
  });

  const generateLinkHref = (attribute, prevActive) => {
    const nextSearchParams = new URLSearchParams(searchParams.toString());
    nextSearchParams.delete('sort_by');
    const sortQuery = sortQueryMap[attribute];

    if (!prevActive && sortQuery) {
      nextSearchParams.append('sort_by', sortQuery);
    }

    if (isSearchPage) {
      nextSearchParams.delete('page');
    }

    const nextQueryStr = nextSearchParams.toString();

    if (isSearchPage) {
      return `/search.html?${nextQueryStr}`;
    }

    return `/${excludePaginationPathParts.join('/')}` + (nextQueryStr ? `?${nextQueryStr}` : '');
  };

  const orderSortingList = useCallback(
    list => {
      return list.sort((a, b) => {
        return a.text.localeCompare(b.text, locale, {
          sensitivity: 'base',
        });
      });
    },
    [locale],
  );

  const sortMethodsFromConfig = availableSortMethods
    ? availableSortMethods
        .map(method => {
          const { value, label } = method;

          if (value !== 'price' && value !== 'position') {
            if (value === 'low_to_high') {
              return {
                id: `sortItem.${value}`,
                text: label,
                attribute: value,
                sortDirection: 'ASC',
              };
            }

            return {
              id: `sortItem.${value}`,
              text: label,
              attribute: value,
              sortDirection: 'DESC',
            };
          }
        })
        .filter(method => !!method)
    : null;

  const sortElements = (() => {
    const defaultSortMethods = [];

    if (!sortState.sortFromSearch) {
      defaultSortMethods.push({
        id: 'sortItem.position',
        text: 'Featured',
        attribute: 'position',
        sortDirection: 'ASC',
      });
    }

    const allSortingMethods = sortMethodsFromConfig
      ? orderSortingList([sortMethodsFromConfig, defaultSortMethods].flat())
      : defaultSortMethods;

    const itemElements = Array.from(allSortingMethods, sortItem => {
      const { attribute, sortDirection } = sortItem;
      const isActive = sortState.sortAttribute === attribute && sortState.sortDirection === sortDirection;
      const key = `${attribute}--${sortDirection}`;

      return (
        <li
          key={key}
          className='flex items-center justify-start'
        >
          <HoverPrefetchLink
            href={generateLinkHref(attribute, isActive)}
            onClick={() => {
              setOpen(false);
            }}
          >
            <span
              className={`inline-block min-w-[140px] whitespace-nowrap text-left font-sans leading-[1.4] hover_font-[600] lg_hover_text-primary ${isActive ? 'font-[600] text-primary' : 'text-title'}`}
            >
              {sortItem.text}
            </span>
          </HoverPrefetchLink>
        </li>
      );
    });

    return (
      <ul className='flex min-w-[160px] flex-col gap-[18px] px-5 py-2.5 text-left shadow-custom-card'>
        {itemElements}
      </ul>
    );
  })();

  const sortTriggerBtn = (
    <div
      className='relative flex h-[60px]  basis-1/2 cursor-pointer items-center justify-between px-[15px] font-poppins font-[600] leading-[1.4] text-title lg_w-auto lg_basis-auto lg_justify-end'
      aria-busy='false'
    >
      <span className='mr-[20px] flex items-center justify-start'>
        <span className='font-poppins font-[600] leading-[1.4] text-title'>
          <span className='lg_uppercase'>Sort By</span>
          <span className='hidden lg_inline'>:</span>
          <span className='ml-[6px] hidden font-sans font-normal lg_inline'>{sortState.sortText}</span>
          {urlHasSort ? <span className='ml-2'>(1)</span> : null}
        </span>
      </span>
      <div className='flex h-5 w-5 flex-none items-center justify-center'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='12'
          height='8'
          fill='none'
          viewBox='0 0 12 8'
        >
          <path
            fill='#666'
            d='M12 1.551c0 .157-.028.312-.083.453a1 1 0 0 1-.234.365L6.54 7.429a.77.77 0 0 1-.544.239.77.77 0 0 1-.544-.238L.31 2.19a1.14 1.14 0 0 1-.306-.71C-.017 1.203.053.927.198.713A.82.82 0 0 1 .78.34a.76.76 0 0 1 .628.238L6 5.26 10.594.734A.8.8 0 0 1 11.036.5a.72.72 0 0 1 .475.093.94.94 0 0 1 .36.392c.086.17.13.366.129.566'
          ></path>
        </svg>
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>{sortTriggerBtn}</PopoverTrigger>
      <PopoverContent
        sideOffset={0}
        className={clsx('shadow-md z-[70] bg-white')}
      >
        {sortElements}
      </PopoverContent>
    </Popover>
  );
};

export default ProductSort;
